import { useCallback, useEffect, useMemo, useState } from "react";

import { getRateFilterOptions, getRates } from "../api/rateApi";
import { mapSorter } from "../components/rate/rateUi";

const DEFAULT_PAGE_SIZE = 20;

/** Loads the paged Ханш list together with its filter options. */
export default function useRateList() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [total, setTotal] = useState(0);

    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    // Column-header filters. Each entry is the array antd hands back from Table onChange.
    const [columnFilters, setColumnFilters] = useState({});
    const [executedDateRange, setExecutedDateRange] = useState([]);

    const [sortField, setSortField] = useState("executedDate");
    const [sortOrder, setSortOrder] = useState("desc");

    const [filterOptions, setFilterOptions] = useState({
        currencies: [],
        institutions: [],
        names: [],
        totalCount: 0,
    });

    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedSearch(search), 350);
        return () => window.clearTimeout(timer);
    }, [search]);

    /**
     * Identity-stable view of the column filters.
     *
     * antd hands back a fresh filters object on every table event, paging included. Comparing the
     * serialised form keeps paging from being mistaken for a filter change.
     */
    const columnFilterKey = useMemo(() => JSON.stringify(columnFilters), [columnFilters]);

    // A real filter change restarts from the first page; changing page must not.
    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, columnFilterKey, executedDateRange]);

    const params = useMemo(() => {
        const [from, to] = executedDateRange || [];

        return {
            page,
            size: pageSize,
            search: debouncedSearch || undefined,
            currency: columnFilters.currency,
            institution: columnFilters.institution,
            dateFrom: from ? from.format("YYYY-MM-DD") : undefined,
            dateTo: to ? to.format("YYYY-MM-DD") : undefined,
            sortField,
            sortOrder,
        };
    }, [page, pageSize, debouncedSearch, columnFilters, executedDateRange, sortField, sortOrder]);

    const loadRows = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            const data = await getRates(params);
            setRows(Array.isArray(data?.content) ? data.content : []);
            setTotal(Number(data?.totalElements || 0));
        } catch (err) {
            setRows([]);
            setTotal(0);
            setError(err?.message || "Ханш ачаалахад алдаа гарлаа");
        } finally {
            setLoading(false);
        }
    }, [params]);

    const loadFilterOptions = useCallback(async () => {
        try {
            const data = await getRateFilterOptions();
            setFilterOptions({
                currencies: data?.currencies || [],
                institutions: data?.institutions || [],
                names: data?.names || [],
                totalCount: Number(data?.totalCount || 0),
            });
        } catch {
            // Filter options are optional; the table stays usable without them.
        }
    }, []);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    useEffect(() => {
        loadFilterOptions();
    }, [loadFilterOptions]);

    const handleTableChange = useCallback((nextPagination, filters, sorter) => {
        if (nextPagination?.current) setPage(nextPagination.current);
        if (nextPagination?.pageSize) setPageSize(nextPagination.pageSize);

        // Keep only the columns that actually have a selection, so an untouched filter does not
        // send an empty array to the API.
        const next = {};
        Object.entries(filters || {}).forEach(([key, value]) => {
            if (Array.isArray(value) && value.length) next[key] = value;
        });

        // Reuse the previous object when nothing actually changed, otherwise the new identity
        // would look like a filter change and reset the page the user just clicked.
        setColumnFilters((prev) =>
            JSON.stringify(prev) === JSON.stringify(next) ? prev : next
        );

        const mapped = mapSorter(sorter);
        setSortField(mapped.sortField || "executedDate");
        setSortOrder(mapped.sortOrder || "desc");
    }, []);

    const clearFilters = useCallback(() => {
        setSearch("");
        setColumnFilters({});
        setExecutedDateRange([]);
        setPage(1);
    }, []);

    const reload = useCallback(() => {
        loadRows();
        loadFilterOptions();
    }, [loadRows, loadFilterOptions]);

    return {
        rows,
        loading,
        error,

        page,
        pageSize,
        total,

        search,
        setSearch,
        columnFilters,
        executedDateRange,
        setExecutedDateRange,

        filterOptions,

        handleTableChange,
        clearFilters,
        reload,
    };
}
